import React from "react";
import Link from "next/link";
import { Card, CardContent } from "@/components/ui/card";
import { SectionHeader } from "../components/section-header";
import { SectionLoader } from "../components/section-loader";
import { EmptyState } from "@/components/shared/empty-state";
import { HelpCircle, ArrowRight } from "lucide-react";

interface QuestionsForExpertsSectionProps {
  items: any[];
  isLoading: boolean;
}

export function QuestionsForExpertsSection({ items, isLoading }: QuestionsForExpertsSectionProps) {
  const displayItems = items.length > 0 ? items.slice(0, 4) : [];

  if (!displayItems.length && !isLoading) return null;

  return (
    <section className="border-b border-border" aria-labelledby="questions-heading">
      <div className="mx-auto max-w-7xl px-5 py-16 sm:py-20">
        <SectionHeader
          eyebrow="Q&A"
          icon={HelpCircle}
          title="Questions Waiting for Experts"
          href="/qa"
        />
        {isLoading ? (
          <div className="grid gap-4 md:grid-cols-2">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-16 bg-neutral-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : displayItems.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2">
            {displayItems.map((item: any) => (
              <Link key={item.id} href={`/qa/${item.id}`} className="group block">
                <Card className="hover:border-neutral-300 transition-all duration-150">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0 mt-0.5">
                        <HelpCircle className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-heading font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2">
                          {item.title}
                        </h4>
                        <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
                          <span>{item.answerCount ?? 0} answers</span>
                          <span className="text-neutral-300">·</span>
                          <span>{item.viewCount ?? 0} views</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <EmptyState icon={HelpCircle} description="No questions yet." />
        )}
      </div>
    </section>
  );
}